import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import {
  adminIamApi,
  type AdminConsoleUser,
  type AdminIamRole,
  type AdminIamRoleRef,
} from '../../../api/adminConsole';
import { expiryDateInputToIso, isoToExpiryDateInput } from '../../../lib/iamAssignmentStatus';
import { roleAllowsExpiry, roleGrantability } from '../../../lib/iamGrantability';
import { sortRoles } from '../../../lib/adminRolesView';
import { IamErrorAlert } from './IamErrorAlert';

export type AssignmentDialogTarget =
  | { mode: 'grant' }
  | {
      mode: 'edit';
      assignment: { id: string; role: AdminIamRoleRef; reason: string | null; expiresAt: string | null };
    };

function todayInput(): string {
  return isoToExpiryDateInput(new Date().toISOString());
}

/**
 * Grant a role (POST /admin/iam/assignments) or edit an assignment's expiry/reason (PATCH).
 * Roles the viewer cannot grant are greyed out with the reason (delegation rule); the reason is
 * required; Super Admin never takes an expiry. The server's error and code are shown as-is.
 */
export function AssignmentDialog({
  open,
  target,
  user,
  viewer,
  roles,
  assignedRoleIds,
  onClose,
  onDone,
}: {
  open: boolean;
  target: AssignmentDialogTarget;
  user: Pick<AdminConsoleUser, 'id' | 'email'>;
  viewer: AdminConsoleUser | undefined;
  roles: readonly AdminIamRole[];
  assignedRoleIds: readonly string[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const editing = target.mode === 'edit' ? target.assignment : null;
  const [roleId, setRoleId] = useState('');
  const [reason, setReason] = useState('');
  const [expiry, setExpiry] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRoleId(editing?.role.id ?? '');
    setReason(editing?.reason ?? '');
    setExpiry(isoToExpiryDateInput(editing?.expiresAt));
    setTouched(false);
  }, [open, editing]);

  const options = useMemo(() => {
    const taken = new Set(assignedRoleIds);
    return sortRoles(roles.filter((r) => !taken.has(r.id))).map((role) => ({
      role,
      grant: roleGrantability(viewer, role, user.id),
    }));
  }, [roles, assignedRoleIds, viewer, user.id]);

  // Editing a role the viewer cannot see (no iam.roles.read / not loaded): permissions unknown,
  // which roleGrantability treats as non-delegable (only a Super Admin may proceed).
  const selectedRole: (Pick<AdminIamRole, 'id' | 'key' | 'name'> & { permissions: string[] | null }) | null = editing
    ? roles.find((r) => r.id === editing.role.id) ?? { ...editing.role, permissions: null }
    : roles.find((r) => r.id === roleId) ?? null;
  const expiryAllowed = roleAllowsExpiry(selectedRole?.key);
  const editGrant = editing && selectedRole ? roleGrantability(viewer, selectedRole, user.id) : null;

  const expiresAtIso = expiryAllowed ? expiryDateInputToIso(expiry) : undefined;
  const expiryInvalid = expiryAllowed && !!expiry && !expiresAtIso;
  const expiryInPast = !!expiresAtIso && Date.parse(expiresAtIso) <= Date.now();
  const reasonMissing = !reason.trim();

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        return adminIamApi.updateAssignment(editing.id, {
          reason: reason.trim(),
          // Clearing the date removes the expiry; Super Admin is always sent as no expiry.
          expiresAt: expiresAtIso ?? null,
        });
      }
      return adminIamApi.assign({
        userId: user.id,
        roleId,
        reason: reason.trim(),
        ...(expiresAtIso ? { expiresAt: expiresAtIso } : {}),
      });
    },
    onSuccess: async () => {
      await onDone();
      onClose();
    },
  });

  useEffect(() => {
    if (open) save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const blocked = !!editGrant && !editGrant.allowed;
  const canSubmit =
    !save.isPending &&
    !reasonMissing &&
    !expiryInvalid &&
    !expiryInPast &&
    !blocked &&
    (editing ? true : !!roleId && options.some((o) => o.role.id === roleId && o.grant.allowed));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
          {editing ? `Edit ${editing.role.name}` : 'Grant role'}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
        >
          {user.email}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <IamErrorAlert error={save.error} />
        {blocked && editGrant && !editGrant.allowed && <Alert severity="warning">{editGrant.reason}</Alert>}
        {!editing && (
          <TextField
            select
            label="Role"
            size="small"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            fullWidth
            helperText={options.length === 0 ? 'Every role is already assigned.' : 'Greyed-out roles are ones you cannot grant.'}
          >
            {options.map(({ role, grant }) => (
              <MenuItem key={role.id} value={role.id} disabled={!grant.allowed}>
                <ListItemText
                  primary={role.name}
                  secondary={grant.allowed ? role.description || undefined : grant.reason}
                />
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label="Reason"
          size="small"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && reasonMissing}
          helperText={touched && reasonMissing ? 'A reason is required (it is recorded in the audit log).' : ' '}
          fullWidth
          multiline
          minRows={2}
        />
        {expiryAllowed ? (
          <Box>
            <TextField
              label="Expires on (optional)"
              type="date"
              size="small"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              fullWidth
              error={expiryInvalid || expiryInPast}
              helperText={
                expiryInPast
                  ? 'Pick a date in the future.'
                  : 'Leave blank for no expiry. Access ends at the end of that day.'
              }
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayInput() } }}
            />
            {expiry && (
              <Button size="small" sx={{ mt: 0.5 }} onClick={() => setExpiry('')}>
                Remove expiry
              </Button>
            )}
          </Box>
        ) : (
          selectedRole && (
            <Typography variant="caption" color="text.secondary">
              Super Admin assignments never expire.
            </Typography>
          )
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => {
            setTouched(true);
            if (!reasonMissing) save.mutate();
          }}
        >
          {editing ? 'Save' : 'Grant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
